import { invoiceSourceLabels, type BwaReport } from '@forq/finance-data';
import { dateLabel, todayIso } from './period';

/**
 * Druckfertige „operative BWA" im Layout der ARISTA-Vorlage — aus dem, was FORQ selbst SEHEN kann:
 * Umsatz (Ausgangsrechnungen) − Wareneinsatz/Fremdleistungen (Kontierung der Eingangsrechnungen) =
 * Rohertrag, dann die Kostenblöcke aus der Kontierung. Personalkosten und Abschreibungen kommen aus
 * der Lohn-/Finanzbuchhaltung, nicht als Rechnung — sie werden als „aus Fibu" markiert statt als 0 €
 * behauptet, und die Rechnung endet EHRLICH beim Zwischenergebnis (kein Betriebsergebnis).
 *
 * Rein clientseitig gerendert (wie die übrigen FTW-Belege), in einem eigenen Tab geöffnet → Strg+P
 * bzw. „Drucken / als PDF speichern".
 */

const euroFmt = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctFmt = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

function euro(value: number): string {
  return euroFmt.format(value);
}
function esc(value: string | number): string {
  return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

type RowKind = 'line' | 'sum' | 'group' | 'result';

interface BwaSheetRow {
  readonly no: string;
  readonly label: string;
  /** Vorzeichenbehaftet: Umsätze positiv, Abzüge negativ. null bei reinen Gruppenüberschriften. */
  readonly amount: number | null;
  /** Anteil an der Gesamtleistung (%), vorzeichenbehaftet. null = nicht ausweisbar. */
  readonly pct: number | null;
  readonly kind: RowKind;
  readonly note?: string;
  /** Kommt aus der Fibu — der Betrag ist bestenfalls ein Fragment. */
  readonly incomplete?: boolean;
}

/** Baut die Rubriken-Zeilen der operativen BWA aus dem BwaReport (Reihenfolge = ARISTA-Logik). */
function buildRows(report: BwaReport): readonly BwaSheetRow[] {
  const base = report.netRevenue;
  const pctOf = (value: number): number | null => (base === 0 ? null : (value / base) * 100);

  const rows: BwaSheetRow[] = [
    { no: 'U', label: 'Umsatzerlöse', amount: report.revenue, pct: pctOf(report.revenue), kind: 'line' },
  ];
  if (report.creditNotes !== 0) {
    rows.push({ no: '', label: 'Erlösschmälerungen (Gutschriften)', amount: -report.creditNotes, pct: pctOf(-report.creditNotes), kind: 'line' });
  }
  rows.push({ no: 'GL', label: 'Gesamtleistung', amount: report.netRevenue, pct: base === 0 ? null : 100, kind: 'sum' });
  rows.push({
    no: '10', label: 'Wareneinsatz / Fremdleistungen', amount: -report.materialExpense,
    pct: pctOf(-report.materialExpense), kind: 'line', note: 'aus der Kontierung der Eingangsrechnungen',
  });
  rows.push({ no: 'S1', label: 'Rohertrag', amount: report.grossProfit, pct: base === 0 ? null : report.grossProfitRate, kind: 'sum' });

  const costBlocks = report.costBlocks.filter((block) => block.key !== 'material');
  if (costBlocks.length > 0) {
    rows.push({ no: '', label: 'Kosten (aus der Kontierung)', amount: null, pct: null, kind: 'group' });
    for (const block of costBlocks) {
      rows.push({
        no: '', label: block.label, amount: -block.amount,
        // Bei „aus Fibu"-Blöcken ist der Betrag nur ein Fragment — keine irreführende Quote ausweisen.
        pct: block.incompleteFromInvoices ? null : pctOf(-block.amount),
        kind: 'line',
        note: block.incompleteFromInvoices ? block.source : `${block.count} Beleg${block.count === 1 ? '' : 'e'}`,
        incomplete: block.incompleteFromInvoices,
      });
    }
    rows.push({ no: 'K', label: 'Summe Kosten (aus Rechnungen)', amount: -report.operatingCosts, pct: pctOf(-report.operatingCosts), kind: 'sum' });
  }

  rows.push({
    no: 'Z', label: 'Zwischenergebnis', amount: report.interimResult, pct: pctOf(report.interimResult), kind: 'result',
    note: 'vor Personalkosten und Abschreibungen',
  });
  return rows;
}

function amountCell(amount: number | null): string {
  if (amount === null) return '';
  const cls = amount < 0 ? 'bwa-num is-neg' : 'bwa-num';
  return `<td class="${cls}">${euro(amount)}</td>`;
}
function pctCell(pct: number | null): string {
  return `<td class="bwa-num bwa-pct">${pct === null ? '–' : `${pctFmt.format(pct)} %`}</td>`;
}

export function renderBwaHtml(companyName: string, periodLabel: string, report: BwaReport): string {
  const rows = buildRows(report);
  const hasIncomplete = rows.some((row) => row.incomplete);

  const bodyRows = rows.map((row) => {
    if (row.kind === 'group') {
      return `<tr class="bwa-group"><td class="bwa-no"></td><td colspan="3">${esc(row.label)}</td></tr>`;
    }
    const rowClass = row.kind === 'sum' ? 'bwa-sum' : row.kind === 'result' ? 'bwa-result' : '';
    const marker = row.incomplete ? ' <span class="bwa-fibu">aus Fibu</span>' : '';
    const note = row.note ? `<small class="bwa-note">${esc(row.note)}</small>` : '';
    return `<tr class="${rowClass}">
      <td class="bwa-no">${esc(row.no)}</td>
      <td class="bwa-label">${esc(row.label)}${marker}${note}</td>
      ${amountCell(row.amount)}
      ${pctCell(row.pct)}
    </tr>`;
  }).join('');

  const sources = report.revenueBySource.filter((row) => row.amount !== 0);
  const sourceTable = sources.length > 0
    ? `<h2>Umsatz nach Herkunft</h2>
       <table class="bwa"><thead><tr><th class="bwa-label">Herkunft</th><th class="bwa-num">Belege</th><th class="bwa-num">Umsatz netto</th></tr></thead>
       <tbody>${sources.map((row) => `<tr><td class="bwa-label">${esc(invoiceSourceLabels[row.source])}</td><td class="bwa-num">${row.count}</td><td class="bwa-num">${euro(row.amount)}</td></tr>`).join('')}</tbody></table>`
    : '';

  const unassigned = report.unassignedExpense > 0
    ? `<p class="bwa-hint">Nicht zugeordneter Aufwand (Konten außerhalb des Betriebsergebnisses oder unbekannt): <strong>${euro(report.unassignedExpense)} EUR</strong>.</p>`
    : '';

  const fibuFootnote = hasIncomplete
    ? `<p class="bwa-hint"><strong>„aus Fibu":</strong> Personalkosten, betriebliche Steuern und Abschreibungen kommen aus der Lohn- und Finanzbuchhaltung, nicht als Eingangsrechnung. Sie erscheinen hier nur, soweit versehentlich als Rechnung erfasst — also unvollständig.</p>`
    : '';

  return `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>BWA – ${esc(companyName)}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 12px; line-height: 1.45; background: #eee; }
  .toolbar { position: sticky; top: 0; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: #e30613; color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,.18); }
  header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #111; padding-bottom: 8px; margin-bottom: 6mm; }
  .brand { font-size: 20px; font-weight: 800; color: #e30613; }
  .rt { font-size: 18px; font-weight: 800; text-align: right; }
  .rs { font-size: 12px; font-weight: 600; text-align: right; color: #333; }
  .rp { font-size: 11px; color: #555; text-align: right; }
  h2 { font-size: 13px; margin: 8mm 0 2mm; }
  table.bwa { width: 100%; border-collapse: collapse; }
  table.bwa th { background: #fafafa; color: #0f6b5c; font-weight: 700; text-align: left; border-bottom: 1px solid #bbb; padding: 5px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; }
  table.bwa td { padding: 4px 8px; vertical-align: top; border-bottom: 1px solid #eee; }
  .bwa-no { width: 34px; color: #888; font-size: 10px; font-weight: 700; }
  .bwa-label { width: auto; }
  .bwa-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  th.bwa-num { text-align: right; }
  .bwa-pct { width: 84px; color: #555; }
  .bwa-num.is-neg { color: #b00016; }
  .bwa-note { display: block; color: #888; font-size: 10px; }
  .bwa-fibu { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 999px; background: #fff3cd; color: #7a5b00; font-size: 9px; font-weight: 700; letter-spacing: .03em; vertical-align: middle; }
  tr.bwa-group td { padding-top: 6mm; font-weight: 700; color: #444; border-bottom: 1px solid #ccc; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; }
  tr.bwa-sum td { border-top: 1px solid #999; border-bottom: 1px solid #999; font-weight: 700; background: #fafafa; }
  tr.bwa-result td { border-top: 2px solid #111; border-bottom: 2px solid #111; font-weight: 800; font-size: 13px; background: #f4f8f6; }
  .bwa-hint { margin: 4mm 0 0; font-size: 10px; color: #555; }
  footer { margin-top: 10mm; border-top: 1px solid #ccc; padding-top: 4px; font-size: 9px; color: #777; }
  @media print { body { background: #fff; } .toolbar { display: none; } .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; } @page { size: A4; margin: 14mm; } h2, table { break-inside: avoid; } tr { break-inside: avoid; } }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    <header>
      <div class="brand">${esc(companyName)}</div>
      <div>
        <div class="rt">Betriebswirtschaftliche Auswertung</div>
        <div class="rs">operativ · aus den FORQ-Belegen</div>
        <div class="rp">Zeitraum: ${esc(periodLabel)} · Stand ${esc(dateLabel(todayIso()))}</div>
      </div>
    </header>
    <table class="bwa">
      <thead><tr><th class="bwa-no">Nr.</th><th class="bwa-label">Bezeichnung</th><th class="bwa-num">Betrag EUR</th><th class="bwa-num bwa-pct">% v. GL</th></tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
    <p class="bwa-hint"><strong>Kein Betriebsergebnis:</strong> Löhne und Abschreibungen liegen nicht als Rechnung vor — die Auswertung endet ehrlich beim Zwischenergebnis. Das vollständige Betriebsergebnis liefert die BWA des Steuerberaters.</p>
    ${fibuFootnote}
    ${unassigned}
    ${sourceTable}
    <footer>FORQ Auswertung · operative BWA · automatisch aus den aktuellen Belegen erzeugt · % v. GL = Anteil an der Gesamtleistung</footer>
  </div>
</body></html>`;
}

/** Öffnet die gerenderte BWA in einem eigenen Tab (Blob-URL). Strg+P bzw. der Knopf druckt sie. */
export function openBwaDocument(html: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  window.open(url, '_blank');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
